'use client';

import { c as _c } from "react/compiler-runtime";
import type { HofDashboardEntry, HofDashboardForm } from '@/features/hof-dashboard/api/hof-dashboard-data';
import { PRIMARY_BUTTON_CLASS, ProgressLine, SECONDARY_BUTTON_CLASS, SectionHeading, StatusPill } from '@/features/hof-dashboard/components/dashboard-ui';
import { EntryAnswers } from '@/features/hof-dashboard/components/entry-answers';
import { ReviewPanel } from '@/features/hof-dashboard/components/review-panel';
import { HOF_ENTRY_STATUS_LABELS, type HofEntryStatus } from '@/features/hof-dashboard/constants';
import { useWarnBeforeLeaving } from '@/features/hof-dashboard/hooks/use-warn-before-leaving';
import { useWithdrawSubmission } from '@/features/hof-dashboard/hooks/use-withdraw-submission';
import { formatDate, translate, type TextKey } from '@/features/hof-dashboard/texts';
import { entryHeading } from '@/features/hof-dashboard/utils/entry-heading';
import type { SubmissionProgress } from '@/features/hof-dashboard/utils/submission-progress';
import AccordionItem from '@/features/payload-cms/components/accordion/accordion-item';
import { FormBlock } from '@/features/payload-cms/components/form';
import { trpc } from '@/trpc/client';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { Trash2 } from 'lucide-react';
import type React from 'react';
import { useCallback, useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
const ENTRY_TONE: Record<HofEntryStatus, 'done' | 'neutral' | 'attention'> = {
  submitted: 'neutral',
  inReview: 'neutral',
  revisionRequired: 'attention',
  accepted: 'done'
};

/** Takes a submission back after a second, inline question: no dialog over the page. */
const WithdrawAction: React.FC<{
  locale: Locale;
  onWithdraw: () => Promise<void>;
}> = t0 => {
  const $ = _c(31);
  const {
    locale,
    onWithdraw
  } = t0;
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const focusOnMount = _temp;
  if (!asking) {
    let t1;
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = () => setAsking(true);
      t2 = <Trash2 className="h-4 w-4" aria-hidden={true} />;
      $[0] = t1;
      $[1] = t2;
    } else {
      t1 = $[0];
      t2 = $[1];
    }
    let t3;
    if ($[2] !== locale) {
      t3 = translate("withdraw", locale);
      $[2] = locale;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== t3) {
      t4 = <button type="button" className="inline-flex min-h-10 cursor-pointer items-center gap-1.5 rounded-lg px-2 text-sm font-semibold text-gray-500 transition hover:bg-gray-100 hover:text-gray-700" onClick={t1}>{t2}{t3}</button>;
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  let t1;
  if ($[6] !== locale) {
    t1 = translate("withdrawQuestion", locale);
    $[6] = locale;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t1) {
    t2 = <p className="text-sm font-medium text-red-900">{t1}</p>;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== busy || $[11] !== onWithdraw) {
    t3 = () => {
      if (busy) {
        return;
      }
      setBusy(true);
      onWithdraw().finally(() => {
        setBusy(false);
        setAsking(false);
      });
    };
    $[10] = busy;
    $[11] = onWithdraw;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Trash2 className="h-4 w-4" aria-hidden={true} />;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== locale) {
    t5 = translate("withdrawConfirm", locale);
    $[14] = locale;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== busy || $[17] !== t3 || $[18] !== t5) {
    t6 = <button type="button" aria-disabled={busy} className="bg-cevi-red inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-lg px-4 text-sm font-bold text-white transition hover:opacity-90 aria-disabled:opacity-50" onClick={t3}>{t4}{t5}</button>;
    $[16] = busy;
    $[17] = t3;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setAsking(false);
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== locale) {
    t8 = translate("cancel", locale);
    $[21] = locale;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t8) {
    t9 = <button type="button" className={SECONDARY_BUTTON_CLASS} onClick={t7} ref={focusOnMount}>{t8}</button>;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t6 || $[26] !== t9) {
    t10 = <div className="flex gap-2">{t6}{t9}</div>;
    $[25] = t6;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t2) {
    t11 = <div role="group" className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3">{t2}{t10}</div>;
    $[28] = t10;
    $[29] = t2;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
};

/** One submission: where it stands, what the Ressort said and what it answered. */
const EntryBlock: React.FC<{
  entry: HofDashboardEntry;
  heading: string;
  /** Off for the version that counts: the card's own status already says where it stands. */
  showStatus?: boolean;
  /** Set for a reviewer, who answers the submission here instead. */
  reviewFor?: string | undefined;
  /** Whether a reviewer can mark it final: the version that counts of a form of versions. */
  canFinalize?: boolean;
  locale: Locale;
  onWithdraw: (id: string) => Promise<void>;
}> = t0 => {
  const $ = _c(40);
  const {
    entry,
    heading,
    showStatus: t1,
    reviewFor,
    canFinalize: t2,
    locale,
    onWithdraw
  } = t0;
  const showStatus = t1 === undefined ? true : t1;
  const canFinalize = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== heading) {
    t3 = <h4 className="text-sm font-bold text-gray-900">{heading}</h4>;
    $[0] = heading;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== entry.status || $[3] !== locale || $[4] !== showStatus) {
    t4 = showStatus && <StatusPill tone={ENTRY_TONE[entry.status]}>{HOF_ENTRY_STATUS_LABELS[entry.status][locale]}</StatusPill>;
    $[2] = entry.status;
    $[3] = locale;
    $[4] = showStatus;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== entry.submittedAt || $[7] !== locale) {
    t5 = translate("submittedOn", locale, {
      date: formatDate(entry.submittedAt, locale)
    });
    $[6] = entry.submittedAt;
    $[7] = locale;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <span className="text-xs text-gray-500">{t5}</span>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t6) {
    t7 = <div className="flex flex-wrap items-center gap-x-3 gap-y-1">{t3}{t4}{t6}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== entry.feedback || $[16] !== entry.feedbackBy || $[17] !== entry.status || $[18] !== locale) {
    t8 = entry.feedback !== undefined && entry.feedback !== "" && <div className={cn("rounded-lg border px-4 py-3 text-sm", entry.status === "revisionRequired" ? "border-amber-200 bg-amber-50 text-amber-900" : "border-gray-200 bg-gray-50 text-gray-700")}><p className="font-semibold">{translate(entry.status === "revisionRequired" ? "feedback" : "lastFeedback", locale)}</p><p className="whitespace-pre-line">{entry.feedback}</p>{entry.feedbackBy !== undefined && <p className="mt-1 text-xs opacity-80">{translate("feedbackBy", locale, {
          name: entry.feedbackBy.name,
          date: formatDate(entry.feedbackBy.at, locale)
        })}</p>}</div>;
    $[15] = entry.feedback;
    $[16] = entry.feedbackBy;
    $[17] = entry.status;
    $[18] = locale;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== entry.answers || $[21] !== locale) {
    t9 = <EntryAnswers answers={entry.answers} locale={locale} />;
    $[20] = entry.answers;
    $[21] = locale;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== canFinalize || $[24] !== entry || $[25] !== locale || $[26] !== reviewFor) {
    t10 = reviewFor !== undefined && <ReviewPanel entry={entry} hofId={reviewFor} canFinalize={canFinalize} locale={locale} />;
    $[23] = canFinalize;
    $[24] = entry;
    $[25] = locale;
    $[26] = reviewFor;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== entry.id || $[29] !== entry.withdrawable || $[30] !== locale || $[31] !== onWithdraw || $[32] !== reviewFor) {
    t11 = entry.withdrawable && reviewFor === undefined && <WithdrawAction locale={locale} onWithdraw={() => onWithdraw(entry.id)} />;
    $[28] = entry.id;
    $[29] = entry.withdrawable;
    $[30] = locale;
    $[31] = onWithdraw;
    $[32] = reviewFor;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t7 || $[37] !== t8 || $[38] !== t9) {
    t12 = <div className="space-y-4">{t7}{t8}{t9}{t10}{t11}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  return t12;
};

/** The label of the button that opens the form, after what there is already. */
const actionLabel = (form: HofDashboardForm): TextKey => {
  const first = form.entries.length === 0;
  if (form.area === 'material') return first ? 'placeOrder' : 'changeOrder';
  if (first) return 'handIn';
  return form.mode === 'versions' ? 'handInNewVersion' : 'handInAnother';
};

/**
 * One form the Hof hands in: where it stands, what it handed in and what the Ressort said, and
 * the form itself, opened in place with the Hof filled in. Of a form of versions the newest is
 * shown and the earlier ones fold away below it.
 */
export const FormCard: React.FC<{
  form: HofDashboardForm;
  progress: SubmissionProgress;
  hofId: string;
  /** A reviewer answers what the Hof handed in, and hands in nothing for it. */
  isReviewer: boolean;
  locale: Locale;
}> = t0 => {
  const $ = _c(78);
  const {
    form,
    progress,
    hofId,
    isReviewer,
    locale
  } = t0;
  const [open, setOpen] = useState(false);
  const [showEarlier, setShowEarlier] = useState(false);
  const utils = trpc.useUtils();
  const withdraw = useWithdrawSubmission(hofId, locale);
  const titleId = useId();
  const earlierId = useId();
  useWarnBeforeLeaving(open);
  let t1;
  let t2;
  if ($[0] !== form.hofField || $[1] !== hofId) {
    t2 = {
      [form.hofField]: hofId
    };
    $[0] = form.hofField;
    $[1] = hofId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  t1 = t2;
  const presetValues = t1;
  let current;
  let earlier;
  if ($[3] !== form.entries) {
    [current, ...earlier] = form.entries;
    $[3] = form.entries;
    $[4] = current;
    $[5] = earlier;
  } else {
    current = $[4];
    earlier = $[5];
  }
  const revisionAsked = progress.gap === "revision";
  let t3;
  if ($[6] !== form.title || $[7] !== titleId) {
    t3 = <span id={titleId}>{form.title}</span>;
    $[6] = form.title;
    $[7] = titleId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== form.area || $[10] !== t3) {
    t4 = <SectionHeading area={form.area}>{t3}</SectionHeading>;
    $[9] = form.area;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== locale || $[13] !== progress) {
    t5 = <ProgressLine progress={progress} locale={locale} />;
    $[12] = locale;
    $[13] = progress;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== form.description) {
    t6 = form.description !== undefined && form.description !== "" && <p className="text-sm text-balance whitespace-pre-line text-gray-600">{form.description}</p>;
    $[15] = form.description;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t6) {
    t7 = <header className="space-y-2">{t4}{t5}{t6}</header>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== form.entries.length || $[22] !== locale || $[23] !== open) {
    t8 = form.entries.length === 0 && !open && <p className="text-sm text-gray-500">{translate("noEntries", locale)}</p>;
    $[21] = form.entries.length;
    $[22] = locale;
    $[23] = open;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== current || $[26] !== earlier || $[27] !== earlierId || $[28] !== form || $[29] !== hofId || $[30] !== isReviewer || $[31] !== locale || $[32] !== showEarlier || $[33] !== withdraw) {
    t9 = form.mode === "versions" && current !== undefined && <><EntryBlock entry={current} heading={entryHeading(form, 0, locale)} showStatus={false} reviewFor={isReviewer ? hofId : undefined} canFinalize={true} locale={locale} onWithdraw={withdraw} />{earlier.length > 0 && <AccordionItem titleElement={<span className="text-sm font-semibold text-gray-700">{translate("earlierVersions", locale, {
          n: earlier.length
        })}</span>} showChevron={true} accordionId={earlierId} isExpanded={showEarlier} onToggle={() => setShowEarlier(_temp2)} isNested={true}>{showEarlier && <div className="divide-y divide-gray-100">{earlier.map((entry, index) => <div key={entry.id} className="py-4 first:pt-0 last:pb-0"><EntryBlock entry={entry} heading={entryHeading(form, index + 1, locale)} locale={locale} reviewFor={isReviewer ? hofId : undefined} onWithdraw={withdraw} /></div>)}</div>}</AccordionItem>}</>;
    $[25] = current;
    $[26] = earlier;
    $[27] = earlierId;
    $[28] = form;
    $[29] = hofId;
    $[30] = isReviewer;
    $[31] = locale;
    $[32] = showEarlier;
    $[33] = withdraw;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== form || $[36] !== hofId || $[37] !== isReviewer || $[38] !== locale || $[39] !== withdraw) {
    t10 = form.mode === "entries" && form.entries.length > 0 && <div className="divide-y divide-gray-100 border-y border-gray-100">{form.entries.map((entry_0, index_0) => <div key={entry_0.id} className="py-4"><EntryBlock entry={entry_0} heading={entryHeading(form, index_0, locale)} locale={locale} reviewFor={isReviewer ? hofId : undefined} onWithdraw={withdraw} /></div>)}</div>;
    $[35] = form;
    $[36] = hofId;
    $[37] = isReviewer;
    $[38] = locale;
    $[39] = withdraw;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== form.closed || $[42] !== form.deadline || $[43] !== locale) {
    t11 = form.closed && form.deadline !== undefined && <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">{translate("formClosed", locale, {
        date: formatDate(form.deadline, locale)
      })}</p>;
    $[41] = form.closed;
    $[42] = form.deadline;
    $[43] = locale;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== form.finalized || $[46] !== isReviewer || $[47] !== locale) {
    t12 = form.finalized && !isReviewer && <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">{translate("finalized", locale)}</p>;
    $[45] = form.finalized;
    $[46] = isReviewer;
    $[47] = locale;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== current || $[50] !== form || $[51] !== isReviewer || $[52] !== locale || $[53] !== open || $[54] !== revisionAsked) {
    t13 = !isReviewer && !form.closed && !form.finalized && !open && <button type="button" className={revisionAsked || current === undefined ? PRIMARY_BUTTON_CLASS : SECONDARY_BUTTON_CLASS} onClick={() => setOpen(true)}>{translate(actionLabel(form), locale)}</button>;
    $[49] = current;
    $[50] = form;
    $[51] = isReviewer;
    $[52] = locale;
    $[53] = open;
    $[54] = revisionAsked;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  let t14;
  if ($[56] !== form.closed || $[57] !== form.finalized || $[58] !== form.form || $[59] !== form.initialValues || $[60] !== hofId || $[61] !== isReviewer || $[62] !== locale || $[63] !== open || $[64] !== presetValues || $[65] !== utils) {
    t14 = !isReviewer && !form.closed && !form.finalized && open && <div className="space-y-2 rounded-xl border border-gray-100 bg-gray-50 p-4 @xl:p-6"><FormBlock form={form.form} withBorder={false} presetValues={presetValues} initialValues={form.initialValues} onSubmitted={() => {
        setOpen(false);
        toast.success(translate("handedIn", locale));
        utils.hofDashboard.getHofDashboard.invalidate({
          hofId
        });
      }} /><button type="button" className={SECONDARY_BUTTON_CLASS} onClick={() => setOpen(false)}>{translate("cancel", locale)}</button></div>;
    $[56] = form.closed;
    $[57] = form.finalized;
    $[58] = form.form;
    $[59] = form.initialValues;
    $[60] = hofId;
    $[61] = isReviewer;
    $[62] = locale;
    $[63] = open;
    $[64] = presetValues;
    $[65] = utils;
    $[66] = t14;
  } else {
    t14 = $[66];
  }
  let t15;
  if ($[67] !== form.id || $[68] !== t10 || $[69] !== t11 || $[70] !== t12 || $[71] !== t13 || $[72] !== t14 || $[73] !== t7 || $[74] !== t8 || $[75] !== t9 || $[76] !== titleId) {
    t15 = <article data-form={form.id} tabIndex={-1} aria-labelledby={titleId} className="scroll-mt-24 space-y-5 rounded-xl border border-gray-100 bg-white p-5 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-green-600 @xl:p-6">{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}</article>;
    $[67] = form.id;
    $[68] = t10;
    $[69] = t11;
    $[70] = t12;
    $[71] = t13;
    $[72] = t14;
    $[73] = t7;
    $[74] = t8;
    $[75] = t9;
    $[76] = titleId;
    $[77] = t15;
  } else {
    t15 = $[77];
  }
  return t15;
};
function _temp(button) {
  return button?.focus();
}
function _temp2(shown) {
  return !shown;
}